// 認証コンテキスト。ログイン直後にクレームを強制リフレッシュして role/companyId を確定させる。
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { auth, refreshClaims, type Claims } from "./firebase";

interface AuthState {
  user: User | null;
  claims: Claims | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Claims | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // ログイン直後・リロード直後はキャッシュ済みトークンの可能性があるため強制リフレッシュ
        setClaims(await refreshClaims(u));
      } else {
        setClaims(null);
      }
      setLoading(false);
    });
  }, []);

  const login = async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    setClaims(await refreshClaims(cred.user));
  };
  const logout = async () => {
    await signOut(auth);
  };

  return <AuthContext.Provider value={{ user, claims, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth は AuthProvider の内側で使ってください");
  return ctx;
}
